import React from 'react';
import { BookOpen, Heart, Activity, Shield, Info } from 'lucide-react';
import SaveToIdeasButton from '../components/SaveToIdeasButton';

const WhatIsCLLPage = () => {
  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 rounded-xl">
              <BookOpen className="h-8 w-8 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-primary mb-1">Information & Education</p>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                What Is CLL?
              </h1>
            </div>
          </div>
          <SaveToIdeasButton
            title="What Is CLL?"
            category="CLL"
            sourcePage="What Is CLL?"
            sourceUrl="/what-is-cll"
            variant="compact"
          />
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        
        {/* Section 1: What is CLL/SLL */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
            What is CLL and SLL?
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              <strong>Chronic Lymphocytic Leukaemia (CLL)</strong> and <strong>Small Lymphocytic Lymphoma (SLL)</strong> are closely related blood cancers that affect the same type of white blood cells called B-lymphocytes. They are essentially the same disease, differing mainly in where the cancer cells are found.
            </p>
            <p>
              In <strong>CLL</strong>, the abnormal lymphocytes are primarily found in the blood and bone marrow. In <strong>SLL</strong>, they're mainly found in the lymph nodes and spleen, with fewer circulating in the blood.
            </p>
            <p>
              Because CLL and SLL are so similar, they are often referred to together as CLL/SLL and are typically treated the same way.
            </p>
            <p className="font-medium text-slate-800">
              Both conditions usually develop slowly, and many people live with them for years without needing treatment.
            </p>
          </div>
        </div>

        {/* Section 2: Why "chronic" */}
        <div className="p-6 md:p-8 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0 mt-1">
              <Activity className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Why CLL is often described as "chronic"
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  CLL is called chronic because it typically progresses gradually. Unlike fast-growing cancers, CLL may remain stable for long periods.
                </p>
                <p className="font-medium text-slate-800 mb-3">For many people:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>It is discovered through routine blood tests</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Symptoms may be mild or absent for years</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Treatment is not required straight away</span>
                  </li>
                </ul>
                <p className="mt-4">
                  This slow progression is why careful monitoring is often preferred over immediate treatment.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: What does CLL affect */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0 mt-1">
              <Shield className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                What does CLL affect?
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">CLL can influence:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>White blood cell counts (especially lymphocytes)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Immune function</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Energy levels</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Infection risk</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Other blood markers, such as platelets or haemoglobin, in later stages</span>
                  </li>
                </ul>
                <p className="mt-4 text-slate-600 italic">
                  Not everyone experiences the same changes, and progression varies widely from person to person.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Reassurance */}
        <div className="p-6 md:p-8 bg-green-50/70">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-green-100 rounded-lg flex-shrink-0 mt-1">
              <Heart className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                An important reassurance
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="text-lg font-medium text-green-800">
                  CLL is highly individual.
                </p>
                <p>
                  Two people with the same diagnosis may have very different experiences. Some never require treatment. Others do, sometimes years after diagnosis.
                </p>
                <p className="font-medium text-slate-800">
                  This platform exists to help people observe, organise, and understand their own journey — not to predict outcomes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Educational Note Footer */}
      <div className="bg-slate-100 rounded-xl p-5 border border-slate-200">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1">Educational note</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              This information is for learning and understanding only. It does not replace medical advice or guidance from a healthcare team.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Save Button */}
      <div className="flex justify-center pt-4">
        <SaveToIdeasButton
          title="What Is CLL?"
          category="CLL"
          sourcePage="What Is CLL?"
          sourceUrl="/what-is-cll"
        />
      </div>
    </div>
  );
};

export default WhatIsCLLPage;
